/**
 * The entry draft: one plain object, edited by pure functions. No React here,
 * so the two rules that are easy to get wrong — what survives a type switch,
 * and what a duplicate must NOT carry — are readable and testable on their own.
 */
import { newEntryProblem, type EntryCategory } from "@core";
import {
  EMPTY_CALC,
  calcFromPaise,
  calcPaise,
  toWireRupees,
  type CalcState,
} from "./money";
import type {
  CategoryKind,
  EntryRecord,
  EntryType,
  PaymentMethod,
} from "./types";

export interface EntryDraft {
  type: EntryType;
  /** The amount lives in the calculator; `calc.entry` is what is on screen. */
  calc: CalcState;
  /** `YYYY-MM-DD`. A STRING end to end — never a `Date` (IST shifts the day). */
  date: string;
  /** Wallet's note + counterParty both land here; AIM has one text column. */
  note: string;

  /**
   * Category is remembered PER KIND, not per screen.
   *
   * Expense→income→expense returns you to the expense category you had picked,
   * because it was never thrown away — it was only hidden while it did not
   * apply. One slot would have to be cleared on every switch, which is exactly
   * the "it forgot what I typed" behaviour the brief rules out.
   */
  categoryIdByKind: Record<CategoryKind, string | null>;

  /** The single account leg an income or expense has. */
  accountId: string;
  /** Transfer legs. Kept separately so switching back and forth is lossless. */
  fromAccountId: string;
  toAccountId: string;

  /** Expense-only, and preserved (not cleared) while another type is showing. */
  paymentMethod: PaymentMethod;

  labelIds: string[];

  /**
   * Wallet's `recordState`. AIM has NO column for this — see the note in
   * `WalletEntry.tsx`; it drives the on-screen state and the optional
   * `record_state` custom field, and nothing else.
   */
  cleared: boolean;
}

/** Today in `YYYY-MM-DD`, built from LOCAL parts — `toISOString()` is UTC and
 *  hands you yesterday's date every evening in IST. */
export function today(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function emptyDraft(type: EntryType = "expense", date: string = today()): EntryDraft {
  return {
    type,
    calc: EMPTY_CALC,
    date,
    note: "",
    categoryIdByKind: { INCOME: null, EXPENSE: null },
    accountId: "",
    fromAccountId: "",
    toAccountId: "",
    paymentMethod: "UPI",
    labelIds: [],
    cleared: true,
  };
}

/** The category kind a type needs, or null for a transfer (which has none). */
export function categoryKindFor(type: EntryType): CategoryKind | null {
  if (type === "income") return "INCOME";
  if (type === "expense") return "EXPENSE";
  return null;
}

/** The currently applicable category id — null on a transfer. */
export function activeCategoryId(draft: EntryDraft): string | null {
  const kind = categoryKindFor(draft.type);
  return kind ? draft.categoryIdByKind[kind] : null;
}

export function setActiveCategoryId(draft: EntryDraft, id: string | null): EntryDraft {
  const kind = categoryKindFor(draft.type);
  if (!kind) return draft;
  return { ...draft, categoryIdByKind: { ...draft.categoryIdByKind, [kind]: id } };
}

/**
 * THE TYPE SWITCH. Everything still meaningful survives; only what cannot
 * apply is set aside (never destroyed).
 *
 * Survives outright: amount, date, note, labels, cleared state, payment
 * method, and BOTH category slots.
 *
 * The account legs are translated rather than copied, because they mean
 * different things:
 *
 *   expense  -> transfer   the expense account is money LEAVING  -> `from`
 *   income   -> transfer   the income account is money ARRIVING  -> `to`
 *   transfer -> expense    money leaves the `from` account        -> account
 *   transfer -> income     money arrives in the `to` account      -> account
 *   income  <-> expense    same single leg, unchanged
 *
 * So picking "HDFC" on an expense and flipping to transfer pre-fills HDFC as
 * the source, not the destination — which is what the number on screen meant
 * one tap ago.
 */
export function switchType(draft: EntryDraft, next: EntryType): EntryDraft {
  if (draft.type === next) return draft;
  const from = draft.type;
  const out: EntryDraft = { ...draft, type: next };

  if (next === "transfer") {
    if (from === "expense") out.fromAccountId = draft.accountId || draft.fromAccountId;
    if (from === "income") out.toAccountId = draft.accountId || draft.toAccountId;
    // Never leave both legs on the same account — the form would be invalid
    // the instant it rendered.
    if (out.fromAccountId && out.fromAccountId === out.toAccountId) {
      if (from === "expense") out.toAccountId = "";
      else out.fromAccountId = "";
    }
    return out;
  }

  if (from === "transfer") {
    out.accountId =
      (next === "expense" ? draft.fromAccountId : draft.toAccountId) || draft.accountId;
  }
  return out;
}

/**
 * "Save and add another": keep the CONTEXT, clear the RECORD.
 *
 * Type, date, accounts, category, payment method and labels stay — they are
 * what makes a run of entries off one receipt bearable. Amount and note go,
 * because carrying them forward is how you silently save the same ₹450 twice.
 */
export function draftForNextEntry(draft: EntryDraft): EntryDraft {
  return { ...draft, calc: EMPTY_CALC, note: "" };
}

/* ── editing and duplicating an existing record ───────────────────────── */

/**
 * Fields of an existing record that a CLONE must never carry.
 *
 * `externalSourceId` is the load-bearing one. It is UNIQUE per user and marks
 * the row a py-rent sync (or a BudgetBakers import) created; the create
 * schemas do not even accept it, so a clone that "helpfully" spread the whole
 * record into the POST body would have that key silently ignored on a good
 * day, and on a bad one would collide with the row it was copied from and be
 * dropped by the server without an error the UI could show. `id` is here for
 * the same reason: a duplicate is a NEW record, so it must reach `POST`, never
 * `PUT /{id}`.
 */
export const NEVER_CLONED = ["id", "externalSourceId"] as const;

/** Seed a draft from a record being EDITED — keeps the identity. */
export function draftFromRecord(record: EntryRecord): EntryDraft {
  const base = emptyDraft(record.type, record.date);
  const kind = categoryKindFor(record.type);
  return {
    ...base,
    calc: calcFromPaise(record.amountPaise),
    note: record.description ?? "",
    categoryIdByKind: kind
      ? { ...base.categoryIdByKind, [kind]: record.categoryId ?? null }
      : base.categoryIdByKind,
    accountId: record.accountId ?? "",
    fromAccountId: record.fromAccountId ?? "",
    toAccountId: record.toAccountId ?? "",
    paymentMethod: (record.paymentMethod as PaymentMethod) ?? "UPI",
    labelIds: record.labelIds ?? [],
  };
}

/**
 * Seed a draft from a record being DUPLICATED.
 *
 * Identical to `draftFromRecord` in what it copies and different in what the
 * CALLER does with it: the returned draft is unattached, so the save path is a
 * `POST`. `NEVER_CLONED` names the two fields that make that true, and
 * `entryBody` below is incapable of emitting either of them.
 */
export function draftFromRecordForClone(record: EntryRecord): EntryDraft {
  // F5: a clone is the same entry on a NEW day — today, editable before saving.
  return { ...draftFromRecord(record), date: today() };
}

/* ── validation and the wire ──────────────────────────────────────────── */

export type DraftProblem =
  | "amount"
  | "category"
  | "account"
  | "from-account"
  | "to-account"
  | "same-account"
  /** F7: a new entry filed on a parent that has sub-categories. */
  | "sub-category"
  /** F7: a new entry with a blank remark (the note). */
  | "remark";

/**
 * The F7 rules for a NEW entry need the category tree (to know whether the
 * chosen category has sub-categories). Omit this — as edits do — and only the
 * structural checks run.
 */
export interface NewEntryRules {
  categories: readonly EntryCategory[];
}

/** Everything wrong with the draft right now, in the order the form reads. */
export function draftProblems(draft: EntryDraft, rules?: NewEntryRules): DraftProblem[] {
  const problems: DraftProblem[] = [];
  if (calcPaise(draft.calc) === null) problems.push("amount");

  if (draft.type === "transfer") {
    if (!draft.fromAccountId) problems.push("from-account");
    if (!draft.toAccountId) problems.push("to-account");
    if (draft.fromAccountId && draft.fromAccountId === draft.toAccountId) {
      problems.push("same-account");
    }
    return problems;
  }

  if (!activeCategoryId(draft)) problems.push("category");
  if (rules) {
    const problem = newEntryProblem(
      { type: draft.type, categoryId: activeCategoryId(draft), remark: draft.note },
      rules.categories,
    );
    if (problem?.field === "categoryId") problems.push("sub-category");
    // The remark is checked on its own so both problems can show at once.
    if (!draft.note.trim()) problems.push("remark");
  }
  return problems;
}

export function canSave(draft: EntryDraft, rules?: NewEntryRules): boolean {
  return draftProblems(draft, rules).length === 0;
}

export interface EntryRequest {
  /** A key of `API_DOMAINS` — never a hardcoded `/api` or `/py-api` prefix. */
  domain: "income" | "expenses" | "transfers";
  /** `""` for a create, `/{id}` for an update. */
  path: string;
  method: "POST" | "PUT";
  body: Record<string, unknown>;
}

/**
 * Build the request for a draft. `recordId` present = update, absent = create.
 *
 * The body carries `amount` in RUPEES because that is the wire contract on all
 * three domains (`CreateIncomeIn.amount`, `TransferCreate.amount`); the server
 * converts with `js_round(amount * 100)`. `toWireRupees` is the single place
 * paise becomes a float, and it happens here, at the boundary, from an exact
 * integer.
 *
 * There is no `externalSourceId` branch. Not "we don't set it" — there is no
 * code path in this function that can emit that key at all.
 */
export function entryBody(draft: EntryDraft, recordId?: string): EntryRequest {
  const paise = calcPaise(draft.calc);
  if (paise === null) throw new Error("Enter an amount");
  const amount = toWireRupees(paise);
  const description = draft.note.trim() || undefined;
  const method = recordId ? "PUT" : "POST";
  const path = recordId ? `/${recordId}` : "";

  if (draft.type === "transfer") {
    return {
      domain: "transfers",
      path,
      method,
      body: {
        date: draft.date,
        amount,
        fromAccountId: draft.fromAccountId,
        toAccountId: draft.toAccountId,
        description,
        labelIds: draft.labelIds,
      },
    };
  }

  const body: Record<string, unknown> = {
    date: draft.date,
    amount,
    categoryId: activeCategoryId(draft),
    description,
    // Explicit null, not omitted: `accountId` is `.optional().nullable()` and
    // null is the meaningful "unlinked" value an edit must be able to restore.
    accountId: draft.accountId || null,
    labelIds: draft.labelIds,
  };
  if (draft.type === "expense") body.paymentMethod = draft.paymentMethod;

  return { domain: draft.type === "income" ? "income" : "expenses", path, method, body };
}

/* ── remembered context (last used per type) ──────────────────────────── */

/**
 * The category / account / payment-mode triple worth remembering after a
 * save, so the next entry of the SAME type can skip re-picking them. Kept
 * PER TYPE (never one global slot) for the same reason `categoryIdByKind`
 * is — an expense's last account and payment mode mean nothing on an income
 * entry, and forcing them across would be exactly the "it forgot what type I
 * was on" bug `categoryIdByKind` already exists to rule out.
 */
export interface EntryContext {
  categoryId: string | null;
  accountId: string;
  paymentMethod: PaymentMethod;
}

export function contextFromDraft(draft: EntryDraft): EntryContext {
  return {
    categoryId: activeCategoryId(draft),
    accountId: draft.type === "transfer" ? draft.fromAccountId : draft.accountId,
    paymentMethod: draft.paymentMethod,
  };
}

/**
 * Layer a remembered context onto a draft. Every field is fill-if-empty: a
 * value the draft already carries — whether from the single-spendable-account
 * auto-fill, a template, or the user's own tap — always wins. This is what
 * makes the remember-last-context feature ADDITIVE rather than a competing
 * default.
 */
export function applyContext(draft: EntryDraft, ctx: EntryContext): EntryDraft {
  let next = draft;
  if (ctx.categoryId && !activeCategoryId(next)) {
    next = setActiveCategoryId(next, ctx.categoryId);
  }
  if (ctx.accountId) {
    if (next.type === "transfer") {
      if (!next.fromAccountId) next = { ...next, fromAccountId: ctx.accountId };
    } else if (!next.accountId) {
      next = { ...next, accountId: ctx.accountId };
    }
  }
  if (next.type === "expense" && next.paymentMethod !== ctx.paymentMethod) {
    next = { ...next, paymentMethod: ctx.paymentMethod };
  }
  return next;
}

/* ── applying a saved TEMPLATE prefill (V5-35) ────────────────────────── */

/**
 * Structural, not `TemplatePrefill` from `components/templates/types.ts` —
 * this file stays free of other domains' types (see the note at the top of
 * `types.ts`), and the fields actually used are a strict subset anyway.
 */
export interface DraftPrefill {
  amountPaise: number | null;
  categoryId: string | null;
  description: string | null;
  accountId: string | null;
  paymentMethod: string | null;
}

/**
 * A template may legitimately carry a null amount ("ask me every time") or no
 * category — unlike a history suggestion, where both are always present — so
 * every field here is applied only when present, same shape as `entryBody`'s
 * guards.
 */
export function applyDraftPrefill(draft: EntryDraft, p: DraftPrefill): EntryDraft {
  let next = draft;
  if (p.amountPaise !== null) next = { ...next, calc: calcFromPaise(p.amountPaise) };
  if (p.categoryId) next = setActiveCategoryId(next, p.categoryId);
  if (p.description) next = { ...next, note: p.description };
  if (p.accountId && next.type !== "transfer") next = { ...next, accountId: p.accountId };
  if (p.paymentMethod && next.type === "expense") {
    next = { ...next, paymentMethod: p.paymentMethod as PaymentMethod };
  }
  return next;
}
